import {FC, ReactNode, useState} from "react";
import {Pen} from "lucide-react";

interface Props {
  value: string;
  icon: ReactNode;
  label: string;
  linkStyle: {backgroundColor: string; color: string};
  editable: boolean;
  onSave: (url: string) => void;
  disabled?: boolean;
}

const EditableLink: FC<Props> = ({value, icon, label, linkStyle, editable, onSave, disabled}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editable && !value) return null;

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          placeholder={`${label} URL`}
          className="text-xs rounded-lg px-2.5 py-0 h-5 border outline-none w-48"
          style={{backgroundColor: "#F7F5F2", borderColor: "#E8E4DF", color: "#282D4D"}}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setEditing(false);
              onSave(draft);
            }
            if (e.key === "Escape") {
              setEditing(false);
              setDraft(value);
            }
          }}
        />
        <button
          onClick={() => {
            setEditing(false);
            onSave(draft);
          }}
          className="text-xs font-bold text-brand-navy hover:text-brand-orange transition-colors"
        >
          ✓
        </button>
        <button
          onClick={() => {
            setEditing(false);
            setDraft(value);
          }}
          className="text-xs text-brand-muted hover:text-brand-navy transition-colors"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {value ? (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium hover:opacity-80 transition-opacity"
          style={linkStyle}
        >
          {icon}
          {label}
        </a>
      ) : editable ? (
        <button
          onClick={() => setEditing(true)}
          className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium border border-dashed border-brand-border text-brand-muted hover:bg-brand-cream transition-colors"
        >
          {icon}
          Add {label}
        </button>
      ) : null}
      {editable && value && (
        <button
          onClick={() => setEditing(true)}
          disabled={disabled}
          className="w-5 h-5 flex items-center justify-center rounded hover:bg-brand-cream text-brand-muted transition-colors disabled:opacity-50"
        >
          <Pen className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

export default EditableLink;
